import {
  BOM_REQUIRED_FIELDS,
  type TBomRequiredField,
  VALUE_CHAIN_AI_AGENT,
} from 'constants/general';

import type { IAICardSelection } from 'components/AICardPanel/interfaces/IAICardSelection';
import type { TAICreatorBoMUnitsTabStatus } from 'components/AICreatorBoMUnits/interfaces/IAICreatorBoMUnitsTab';
import type IAICreatorBoMUnitsTabItem from 'components/AICreatorBoMUnits/interfaces/IAICreatorBoMUnitsTabItem';
import type ISupplierDiscoveryTableItem
  from 'components/AICreatorSupplierUnits/interfaces/ISupplierDiscoveryTableItem';
import type ISupplierMissingField
  from 'components/AICreatorSupplierUnits/interfaces/ISupplierMissingField';
import type IOption from 'components/MappingModal/interfaces/IOption';
import type { IVCAgentSessionUnitUpdatedPayload } from 'hooks/interfaces/IUseWebSocket';
import type { SetStateAction } from 'react';
import type IAgentSessionBomLineItem from 'store/slices/valueChainMap/interfaces/IAgentSessionBomLineItem';
import type IAgentSessionBomProducedByUnit from 'store/slices/valueChainMap/interfaces/IAgentSessionBomProducedByUnit';
import type ISelectedUnits from 'store/slices/valueChainMap/interfaces/ISelectedUnits';
import type ISessionVCItem from 'store/slices/valueChainMap/interfaces/ISessionVCItem';

type TAIGeneratingStatus = typeof VALUE_CHAIN_AI_AGENT.STATUS_DONE
  | typeof VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS
  | typeof VALUE_CHAIN_AI_AGENT.STATUS_EMPTY;

export const getUnitIdFromNodeId = (nodeId: string): number | null => {
  const trimmedNodeId = nodeId.trim();
  const withPrefixMatch = trimmedNodeId.match(
    new RegExp(`^${VALUE_CHAIN_AI_AGENT.UNIT_NODE_PREFIX}(\\d+)$`),
  );

  if (withPrefixMatch) {
    const matchedUnitId = Number(withPrefixMatch[1] || '');
    if (Number.isInteger(matchedUnitId) && matchedUnitId > 0) {
      return matchedUnitId;
    }
  }
  const plainUnitId = Number(trimmedNodeId);
  if (Number.isInteger(plainUnitId) && plainUnitId > 0) {
    return plainUnitId;
  }
  return null;
};

export const flattenNaceOptions = (options: IOption[] = []): IOption[] => options.flatMap((option) => {
  const divisions = option.divisions || [];

  return [
    {
      value: option.value,
      label: option.label || option.name || option.value,
    },
    ...divisions.map((division) => ({
      value: division.value,
      label: division.label || division.name || division.value,
    })),
  ];
});

export const getSortedUnitIds = (cards: { nodeId: string }[]): number[] => cards.reduce<number[]>(
  (ids, card) => {
    const unitId = getUnitIdFromNodeId(card.nodeId);

    if (unitId === null) {
      return ids;
    }

    ids.push(unitId);

    return ids;
  },
  [],
).sort((firstId, secondId) => firstId - secondId);

export const areUnitIdsEqual = (
  firstCards: { nodeId: string }[],
  secondCards: { nodeId: string }[],
): boolean => {
  const firstIds = getSortedUnitIds(firstCards);
  const secondIds = getSortedUnitIds(secondCards);

  return firstIds.length === secondIds.length
    && firstIds.every((unitId, index) => unitId === secondIds[index]);
};

export const normalizeTabStatus = (status?: string): TAICreatorBoMUnitsTabStatus => {
  if (status === VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT) {
    return VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT;
  }

  if (status === VALUE_CHAIN_AI_AGENT.AI_STATUS) {
    return VALUE_CHAIN_AI_AGENT.AI_STATUS;
  }

  if (
    status === VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS
    || status === VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS_LEGACY
    || status === VALUE_CHAIN_AI_AGENT.STATUS_PROCESSING
  ) {
    return VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS;
  }

  return VALUE_CHAIN_AI_AGENT.STATUS_EMPTY;
};

export const isVcAgentBomSocketInProgressStatus = (status?: string): boolean => {
  if (!status) {
    return false;
  }

  return (
    status === VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS
    || status === VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS_LEGACY
    || status === VALUE_CHAIN_AI_AGENT.STATUS_PROCESSING
  );
};

export const isVcAgentBomSocketProcessingStatus = (status?: string): boolean => (
  status === VALUE_CHAIN_AI_AGENT.STATUS_PROCESSING
);

export const isAIAgentSessionReviewReadyStatus = (status?: string): boolean => (
  status === VALUE_CHAIN_AI_AGENT.STATUS_REVIEW
  || status === VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT
);

export const normalizeAIGeneratingStatus = (
  status?: string,
): TAIGeneratingStatus => {
  if (
    status === VALUE_CHAIN_AI_AGENT.STATUS_DONE
    || isAIAgentSessionReviewReadyStatus(status)
  ) {
    return VALUE_CHAIN_AI_AGENT.STATUS_DONE;
  }

  if (isVcAgentBomSocketInProgressStatus(status)) {
    return VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS;
  }

  return VALUE_CHAIN_AI_AGENT.STATUS_EMPTY;
};

export const normalizeAIGeneratingModalTabDisplayStatus = (
  status?: string,
): TAIGeneratingStatus => {
  if (
    status === VALUE_CHAIN_AI_AGENT.AI_STATUS
    || status === VALUE_CHAIN_AI_AGENT.STATUS_PROCESSING
  ) {
    return VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS;
  }

  return VALUE_CHAIN_AI_AGENT.STATUS_DONE;
};

export const getSessionSelectedUnitLiveStatus = (
  sessionId: number | undefined,
  selectedUnit: ISelectedUnits,
  lastBomSocketPayload?: IVCAgentSessionUnitUpdatedPayload | null,
): string | undefined => {
  if (
    sessionId
    && lastBomSocketPayload
    && lastBomSocketPayload.sessionId === sessionId
    && lastBomSocketPayload.unitId === selectedUnit.unitId
  ) {
    return lastBomSocketPayload.status;
  }

  return selectedUnit.status;
};

export const getAIGeneratingSessionStatus = (
  session?: ISessionVCItem,
  lastBomSocketPayload?: IVCAgentSessionUnitUpdatedPayload | null,
): TAIGeneratingStatus => {
  if (!session) {
    return VALUE_CHAIN_AI_AGENT.STATUS_EMPTY;
  }

  const normalizedSessionStatus = normalizeAIGeneratingModalTabDisplayStatus(session.status);

  if (normalizedSessionStatus === VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS) {
    return VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS;
  }

  const selectedUnits = session.selectedUnits || [];

  if (selectedUnits.length > 0) {
    const unitDisplayStatuses = selectedUnits.map((selectedUnit) => normalizeAIGeneratingModalTabDisplayStatus(
      getSessionSelectedUnitLiveStatus(session.id, selectedUnit, lastBomSocketPayload),
    ));

    if (unitDisplayStatuses.some((status) => status === VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS)) {
      return VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS;
    }

    return VALUE_CHAIN_AI_AGENT.STATUS_DONE;
  }

  return normalizedSessionStatus;
};

export const buildManufacturerCardsFromUnits = (
  manufacturerCardsByUnitId: Map<number, IAICardSelection>,
  units: IAgentSessionBomProducedByUnit[] = [],
): IAICreatorBoMUnitsTabItem[] => units.reduce<IAICreatorBoMUnitsTabItem[]>((cards, unit) => {
  const matchedCard = manufacturerCardsByUnitId.get(unit.id);

  cards.push({
    ...(matchedCard || {
      nodeId: `${VALUE_CHAIN_AI_AGENT.UNIT_NODE_PREFIX}${unit.id}`,
      name: unit.name,
      type: unit.type,
      swimlaneId: '',
    }),
    status: VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT,
  });

  return cards;
}, []);

export const areAllBomLineItemsFilled = (items: IAgentSessionBomLineItem[]): boolean => items.every((item) => {
  if (!item.isActive) {
    return true;
  }

  const hasName = Boolean(item.name?.trim());
  const hasCode = Boolean(item.code?.trim());
  const hasPotentialSuppliers = item.potentialSuppliers?.some((supplier) => Boolean(supplier.trim()));
  const hasSource = Boolean(item.source?.trim());

  return hasName && hasCode && hasPotentialSuppliers && hasSource;
});

type TBomLineItemNotFilled = {
  lineIndex: number,
  missingFields: TBomRequiredField[],
};

export const hasTextValue = (value?: string | null): boolean => Boolean(value?.trim());

export const emptyToNull = (value: string | null | undefined): string | null => (
  value?.trim() ? value : null
);

export const normalizeSupplierTableItem = (
  item: ISupplierDiscoveryTableItem,
): ISupplierDiscoveryTableItem => ({
  ...item,
  isActive: item.isActive ?? true,
});

export const normalizeSupplierItem = normalizeSupplierTableItem;

export const getTableCacheKey = (
  mapId?: string,
  agentSessionId?: number,
  agentUnitId?: number | null,
  contextKey?: string,
) => {
  if (!mapId || !agentSessionId || !agentUnitId || !contextKey) {
    return '';
  }

  return [mapId, agentSessionId, agentUnitId, contextKey].join(':');
};

export const mergeSupplierItems = (
  apiItems: ISupplierDiscoveryTableItem[],
  cachedItems: ISupplierDiscoveryTableItem[],
) => {
  if (!cachedItems.length) {
    return apiItems;
  }

  const cachedItemsById = new Map(cachedItems.map((cachedItem) => [cachedItem.id, cachedItem]));
  const apiItemIds = new Set(apiItems.map((apiItem) => apiItem.id));
  const mergedApiItems = apiItems.map((apiItem) => cachedItemsById.get(apiItem.id) || apiItem);
  const cachedOnlyItems = cachedItems.filter((cachedItem) => !apiItemIds.has(cachedItem.id));

  return [...mergedApiItems, ...cachedOnlyItems];
};

export const resolveTableItemsUpdate = (
  previousItems: ISupplierDiscoveryTableItem[],
  updater: SetStateAction<ISupplierDiscoveryTableItem[]>,
) => (typeof updater === 'function' ? updater(previousItems) : updater);

export const getSupplierItemsNotFilled = (
  items: ISupplierDiscoveryTableItem[],
): ISupplierMissingField[] => items.reduce<ISupplierMissingField[]>((missingFields, item) => {
  if (!item.isActive) {
    return missingFields;
  }

  if (!hasTextValue(item.name)) {
    missingFields.push({ itemId: item.id, field: 'name' });
    return missingFields;
  }

  if (!hasTextValue(item.code)) {
    missingFields.push({ itemId: item.id, field: 'code' });
    return missingFields;
  }

  if (!hasTextValue(item.addressCountry)) {
    missingFields.push({ itemId: item.id, field: 'location' });
    return missingFields;
  }

  return missingFields;
}, []);

export const getBomLineItemsNotFilled = (
  items: IAgentSessionBomLineItem[],
): TBomLineItemNotFilled[] => items.reduce<TBomLineItemNotFilled[]>((acc, item, lineIndex) => {
  if (!item.isActive) {
    return acc;
  }

  const missingFields: TBomRequiredField[] = [];

  const hasName = Boolean(item.name?.trim());
  if (!hasName) {
    missingFields.push(BOM_REQUIRED_FIELDS.NAME);
  }

  const hasCode = Boolean(item.code?.trim());
  if (!hasCode) {
    missingFields.push(BOM_REQUIRED_FIELDS.CODE);
  }

  const hasPotentialSuppliers = item.potentialSuppliers?.some((supplier) => Boolean(supplier.trim()));
  if (!hasPotentialSuppliers) {
    missingFields.push(BOM_REQUIRED_FIELDS.SUPPLIERS);
  }

  const hasSource = Boolean(item.source?.trim());
  if (!hasSource) {
    missingFields.push(BOM_REQUIRED_FIELDS.SOURCE);
  }

  if (missingFields.length) {
    acc.push({ lineIndex, missingFields });
  }

  return acc;
}, []);
